import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments } from '@fortawesome/free-solid-svg-icons';
import { paths } from '../../../routing/path';
import { QueryParams } from '../../../utils/queryParams';
import { useStateSelector } from '../../../store/selectors';
import { useCurrentMarketplaceParty } from '../../../custom-hooks/useCurrentMarketplaceParty';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { SellerMarketplaceTab } from '../../../slices/marketplace/timeline/models/enums/seller-marketplace-tab.enum';
import { BuyerOppMarketplaceTab } from '../../../slices/marketplace/timeline/models/enums/buyer-opportunity-details-tab.enum';
import { MarketplaceCommsHubTab } from '../../AdminMarketplace/MarketplaceTabs/MarketplaceCommsHub/MarketplaceCommsHubTabs';
import { CreateClosingDateDiscussion } from '../../../slices/marketplace/closing-date/thunks/create-closing-date-discussion.thunk';

export interface ClosingDateSchedulingBlockProps {
    engagementId: number;
    threadedDiscussionId?: number | null;
    commsId?: number | null;
    currentUserParty?: TimelineParty | null;
    isEngagementClosed: boolean;
}

export const ClosingDateSchedulingBlock = ({
    engagementId,
    threadedDiscussionId,
    commsId,
    currentUserParty,
    isEngagementClosed,
}: ClosingDateSchedulingBlockProps) => {
    const dispatch = useDispatch() as any;
    const navigate = useNavigate();
    const party = useCurrentMarketplaceParty(engagementId);
    const isBuyer = party === TimelineParty.Buyer;
    const isSubmitting = useStateSelector(
        (state) => state.closingDateState.isSubmitting
    );
    const isDiscussionCreated = !!threadedDiscussionId && !!commsId;
    const canCreateDiscussion = !!currentUserParty && !isEngagementClosed;
    const isInteractive = isDiscussionCreated || canCreateDiscussion;

    const navigateToDiscussion = (
        discussionCommsId: number,
        discussionId: number
    ) => {
        const commsHubTab = isBuyer
            ? BuyerOppMarketplaceTab.CommsHub
            : SellerMarketplaceTab.CommsHub;

        if (isBuyer) {
            navigate(
                `${paths.venues.connect.opportunities}?opportunityId=${engagementId}&tab=${commsHubTab}&subtab=${MarketplaceCommsHubTab.BuyerCommunications}&${QueryParams.CommsIdKey}=${discussionCommsId}&${QueryParams.DiscussionIdKey}=${discussionId}`
            );
        } else {
            navigate(
                `${paths.venues.connect.engagement(engagementId)}?tab=${commsHubTab}&subtab=${MarketplaceCommsHubTab.BuyerCommunications}&${QueryParams.CommsIdKey}=${discussionCommsId}&${QueryParams.DiscussionIdKey}=${discussionId}`
            );
        }
    };

    const handleClick = async () => {
        if (isSubmitting || !isInteractive) {
            return;
        }
        if (isDiscussionCreated) {
            navigateToDiscussion(commsId as number, threadedDiscussionId as number);
            return;
        }
        try {
            const result = await dispatch(
                CreateClosingDateDiscussion(engagementId)
            ).unwrap();
            if (result.threadedDiscussionId && result.commsId) {
                navigateToDiscussion(
                    result.commsId,
                    result.threadedDiscussionId
                );
            }
        } catch {
            return;
        }
    };

    return (
        <div
            className="closing-date-scheduling-block card"
            role={isInteractive ? 'button' : undefined}
            aria-disabled={!isInteractive || isSubmitting}
            tabIndex={isInteractive ? 0 : undefined}
            onClick={isInteractive ? handleClick : undefined}
            onKeyDown={
                isInteractive
                    ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleClick();
                          }
                      }
                    : undefined
            }>
            <div className="closing-date-scheduling-block__icon">
                <FontAwesomeIcon icon={faComments} />
            </div>
            <div className="closing-date-scheduling-block__text">
                <div className="closing-date-scheduling-block__title">
                    Closing Date Scheduling
                </div>
                <div className="closing-date-scheduling-block__subtitle">
                    {isDiscussionCreated
                        ? 'Discussion board created · Threaded discussion with all parties'
                        : 'Open a threaded discussion to coordinate the date with all parties'}
                </div>
            </div>
        </div>
    );
};
